/* eslint-disable jsx-a11y/anchor-is-valid, jsx-a11y/label-has-for */
import { map, result } from 'underscore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { MessageBar, MessageBarType } from '@fluentui/react';
import { registerIcons } from '@fluentui/style-utilities';
import { Glyphicon } from '@bingads-webui-react/primitive-unthemed';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  DisplayUrlModes,
  WebsiteDescriptionLanguages,
  getWebsiteDescriptionList,
  getWebsiteDescriptionInAnotherLanguage,
  isWebsiteDescriptionUnspecified,
} from './options';
import { getStyles } from './styles';

registerIcons({
  icons: {
    Insights: <Glyphicon glyph="ba-Insights" />,
  },
});

const messageBarStyle = {
  root: {
    background: '#EFF6FC',
    '& span.iconba': {
      fontSize: '16px',
      color: '#0078D4',
      verticalAlign: 'top',
    },
  },
};

export const VanityUrlOption = ({
  i18n,
  vanityUrlSettings,
  onChange,
}) => {
  const classes = useDefaultStyles(getStyles);

  const [displayUrlMode, setDisplayUrlMode] = useState(result(vanityUrlSettings, 'DisplayUrlMode', DisplayUrlModes[0]));
  const [websiteDescription, setWebsiteDescription] = useState(result(vanityUrlSettings, 'WebsiteDescription', 'Unspecified'));

  const languages = useMemo(() => map(WebsiteDescriptionLanguages, language => ({ key: language, value: i18n.getString(language) })), [i18n]);
  const { language, websiteDescriptions } = useMemo(() => getWebsiteDescriptionList(websiteDescription), [websiteDescription]);

  const onDisplayUrlModeChange = useCallback((e) => {
    setDisplayUrlMode(e.target.value);
    if (e.target.value === DisplayUrlModes[1] && isWebsiteDescriptionUnspecified(websiteDescription)) {
      setWebsiteDescription(websiteDescriptions[0].key);
    }
  }, [websiteDescription, websiteDescriptions]);

  const onLanguageChange = useCallback((e) => {
    setWebsiteDescription(getWebsiteDescriptionInAnotherLanguage(websiteDescription, e.target.selectedIndex));
  }, [websiteDescription]);

  const onWebsiteDescriptionChange = useCallback((e) => {
    setWebsiteDescription(e.target.value);
  }, []);

  useEffect(() => {
    onChange({
      DisplayUrlMode: displayUrlMode,
      WebsiteDescription: displayUrlMode === DisplayUrlModes[0] ? 'Unspecified' : websiteDescription,
    });
  }, [displayUrlMode, onChange, websiteDescription]);

  return (
    <div className={classnames('form-group', 'vanity-url-option', classes.vanityUrlOption)}>
      <h5 className="col-sm-2 control-label">
        <label>{i18n.getString(_TL_('Display URL options'))}</label>
        <HelpPopup
          topic="pop_BA_CampaignCreate_DisplayURLOptions"
          aria-label={i18n.getString(_TL_('Learn more about Display URL options'))}
        />
      </h5>
      <div className="col-sm-6">
        <div className="radio">
          <label>
            <input
              type="radio"
              name="displayUrlMode"
              className="final-url-radio"
              value={DisplayUrlModes[0]}
              checked={displayUrlMode === DisplayUrlModes[0]}
              onChange={onDisplayUrlModeChange}
            />
            {i18n.getString(_TL_('Show Final URL'))}
          </label>
          <div className="form-group-subset">
            <div className="form-control-static">{i18n.getString(_TL_('The Final URL will appear in your ad.'))}</div>
          </div>
        </div>
        <div className="radio">
          <label>
            <input
              type="radio"
              name="displayUrlMode"
              className="website-description-radio"
              value={DisplayUrlModes[1]}
              checked={displayUrlMode === DisplayUrlModes[1]}
              onChange={onDisplayUrlModeChange}
            />
            {i18n.getString(_TL_('Show website description'))}
          </label>
          <div className="form-group-subset">
            <div className="form-control-static">{i18n.getString(_TL_('A description of your website will appear in your ad instead of a URL. When potential customers click the website description, they will be taken to your Final URL. Choose a website description from the dropdown menu.'))}</div>
            <MessageBar
              className="message-bar website-description-info"
              messageBarIconProps={{ iconName: 'Insights' }}
              messageBarType={MessageBarType.info}
              styles={messageBarStyle}
            >
              {i18n.getString(_TL_('Recommended for pharmaceutical ads.'))}
            </MessageBar>
            <div hidden={displayUrlMode === DisplayUrlModes[0]}>
              <div className="website-description-options">
                <select
                  className="form-control language-select"
                  aria-label={i18n.getString(_TL_('Select a language'))}
                  value={language}
                  onChange={onLanguageChange}
                >
                  {languages.map(lang => <option key={lang.key} value={lang.key}>{lang.value}</option>)}
                </select>
                <select
                  className="form-control website-description-select"
                  aria-label={i18n.getString(_TL_('Select a website description'))}
                  value={websiteDescription}
                  onChange={onWebsiteDescriptionChange}
                >
                  {websiteDescriptions.map(description => <option key={description.key} value={description.key}>{description.value}</option>)}
                </select>
              </div>
              <MessageBar
                className="message-bar language-disclaimer"
                messageBarIconProps={{ iconName: 'Info', style: { paddingTop: 2 } }}
                messageBarType={MessageBarType.info}
                styles={messageBarStyle}
              >
                {i18n.getString(_TL_('These website descriptions are shown exactly as they will appear in your ads. Please note that website descriptions aren\'t translated. For example, if your ad appears in French, the website description will remain in English or Spanish based on the option you choose.'))}
              </MessageBar>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

VanityUrlOption.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  vanityUrlSettings: PropTypes.shape({
    DisplayUrlMode: PropTypes.oneOf(DisplayUrlModes),
    WebsiteDescription: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
};

VanityUrlOption.defaultProps = {
  vanityUrlSettings: {
    DisplayUrlMode: DisplayUrlModes[0],
    WebsiteDescription: 'Unspecified',
  },
};
